/**
 * ld-variant-hero-picker.js - make a click on the already-selected option count.
 *
 * SSOT:      theme/assets/ld-variant-hero-picker.js
 * Deployed:  theme/deploy_variant_hero_gallery.py
 * Loads on:  product pages carrying `custom.gallery_mode`, and nowhere else.
 *            The <script> tag is emitted only inside the gated branch of
 *            snippets/product-media-gallery-content.liquid.
 * Requires:  Horizon's <variant-picker> (assets/variant-picker.js).
 *
 * The gallery on a gated product shows the unattached composite until a variant
 * is named in the URL. One option is pre-checked on arrival, so clicking THAT
 * option is not a change: the radio is already checked, no `change` event
 * fires, the URL never gains `?variant=`, the section never re-renders, and the
 * shopper sees nothing move. The other option works, because it is a real
 * change. Shipping a picker that responds on one of two paths is the same
 * complaint the gallery work exists to fix.
 *
 * This makes that one click a real selection by calling the theme's own two
 * public methods - `buildRequestUrl` then `fetchUpdatedSection` - which between
 * them dispatch ProductSelectEvent, fetch the section, and morph it in. Both
 * read the picker's live `input:checked` state, which is already correct,
 * so neither needs anything set up first.
 *
 * 🔴 IT DOES NOT DISPATCH A SYNTHETIC `change`, AND THAT IS DELIBERATE.
 * Dispatching one works, but it routes through `updateSelectedOption`, whose
 * job is to move the checked state from one option to another. Re-selecting
 * the option that is ALREADY checked pushes the same index onto its two-slot
 * history, so the option ends up marked as both current and previous and
 * settles on `data-current-checked="false"` - on the option the shopper just
 * chose. Measured 2026-08-20: `checked=true cur=false prev=true` after a
 * dispatched change, against `cur=true prev=false` on a direct load of the same
 * variant. The morph does not repair it. That attribute seeds the picker's
 * state on reconnect and is this file's own discriminator below, so leaving the
 * theme in a state it never produces itself is a trap for whoever reads it
 * next. Skipping that one method avoids the whole problem: the radio is already
 * checked and the pill is already in the right place.
 *
 * Three conditions, all required, and each one is load-bearing:
 *
 *   1. The URL has no `variant` param. Once it does, the hero already matches
 *      the checked option and a click on it SHOULD do nothing - that is stock
 *      behaviour and this file must not override it.
 *   2. `data-current-checked === "true"` on the clicked input. Horizon writes
 *      that attribute server-side and only updates it inside its own change
 *      handler, so at click time it still reads the PRE-click state. That is
 *      the only reliable way to tell "was already checked" from "just became
 *      checked" - `input.checked` is true in both cases by the time any click
 *      listener runs, because a radio's checkedness is set during pre-click
 *      activation, before the event is dispatched.
 *   3. The event target IS the input. A click on the <label> reaches the
 *      document twice - once for the label, once for the click it forwards to
 *      the nested input - and acting on both would fire two section fetches.
 *
 * Scope guards mirror VariantPicker's own: the main product's picker only
 * (`data-template-product-match`), never a product card or the quick-add
 * dialog, so recommendations further down a gated product page are untouched.
 *
 * The listener is attached once, to the document, and delegated. It has to be:
 * a variant change replaces the gallery - and this script tag with it - as
 * injected HTML, which never executes. A listener that re-attached per render
 * would be dead after the first selection.
 *
 * Revisions
 *   2026-08-20 - Call fetchUpdatedSection directly instead of dispatching a
 *                synthetic change. See the note above - the dispatch left
 *                data-current-checked="false" on the chosen option.
 *   2026-08-20 - Initial.
 */

const FLAG = '__ldVariantHeroPicker';

/**
 * @param {MouseEvent} event
 */
function handleClick(event) {
  const input = event.target;

  // Condition 3 - the forwarded click on the input, not the label around it.
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

  // Condition 2 - read Horizon's server-rendered state, not input.checked.
  if (input.dataset.currentChecked !== 'true') return;

  // Condition 1 - only while the page is still in its no-variant landing state.
  if (new URL(window.location.href).searchParams.has('variant')) return;

  const picker = input.closest('variant-picker');
  if (!picker) return;
  if (picker.dataset.templateProductMatch !== 'true') return;
  if (input.closest('product-card') || input.closest('quick-add-dialog')) return;

  // A variant this click cannot resolve to is not ours to handle.
  const { optionValueId = '', variantId = '', connectedProductUrl = '' } = input.dataset;
  if (!variantId) return;

  // Same call VariantPicker makes for a genuine change, minus the checked-state
  // bookkeeping, which has nothing to do here. `buildRequestUrl` reads
  // `input:checked` for its `option_values`, so it resolves to this option.
  picker.fetchUpdatedSection(picker.buildRequestUrl(input), {
    detail: { optionValueId, variantId, connectedProductUrl },
  });

  // And the URL, which is what makes `product.selected_variant` non-nil on a
  // reload or a shared link. `replaceState`, not `pushState`, to match what the
  // theme does for a genuine change - a variant pick is not a history entry.
  const url = new URL(window.location.href);
  url.searchParams.set('variant', variantId);
  history.replaceState({}, '', url.toString());
}

if (!window[FLAG]) {
  window[FLAG] = true;
  document.addEventListener('click', handleClick);
}
